import { motion, useInView } from "framer-motion"
import { graphql, Link } from "gatsby"
import parse from "html-react-parser"
import React, { useEffect, useRef, useState } from "react"
import { Autoplay, Navigation } from "swiper"
import "swiper/css"
import "swiper/css/effect-fade"
import "swiper/css/pagination"
import { Swiper, SwiperSlide } from "swiper/react"
import ImageOpt from "../components/ImageOpt"
import Layout from "../components/layout"
import Seo from "../components/Seo/Seo"

const BlogTemplate = props => {
  const post = props.data.post
  const cta = props.data.wp.themeGeneralSettings.fluxDna

  const [nextPageCursor, setNextPageCursor] = useState("")
  const [prevPageCursor, setPrevPageCursor] = useState("")
  const ref = useRef(null)
  const [hasPlayed, setHasPlayed] = useState(false)
  const isInView = useInView(ref)

  const [copySuccess, setCopySuccess] = useState(null)
  const postUrl = `${process.env.GATSBY_WP_URL}/journal${post.uri}`

  const copyToClipboard = () => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(postUrl).then(
        () => {
          setCopySuccess("Link copied!")
          setTimeout(() => setCopySuccess(null), 2000)
        },
        err => {
          console.error("Failed to copy text: ", err)
          setCopySuccess("Failed to copy link")
        }
      )
    } else {
      const textArea = document.createElement("textarea")
      textArea.value = postUrl
      document.body.appendChild(textArea)
      textArea.select()
      try {
        document.execCommand("copy")
        setCopySuccess("Link copied!")
        setTimeout(() => setCopySuccess(null), 2000)
      } catch (err) {
        console.error("Failed to copy text: ", err)
        setCopySuccess("Failed to copy link")
      }
      document.body.removeChild(textArea)
    }
  }

  const sectionShow = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut",
        delayChildren: 0.5,
        staggerChildren: 0.2,
      },
    },
  }

  const animFade = {
    hidden: { opacity: 0, y: 20 },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: "easeInOut",
        delayChildren: 0.5,
        staggerChildren: 0.2,
      },
    },
  }

  useEffect(() => {
    if (isInView && !hasPlayed) {
      setHasPlayed(true)
    }
  }, [isInView, hasPlayed, setHasPlayed])

  return (
    <div className="fullPageanimation our-journal-single">
      <Layout props={props}>
        <Seo seo={post.seo} />
        <section>
          <motion.section
            initial="hidden"
            animate={isInView || hasPlayed ? "show" : "hidden"}
            variants={sectionShow}
            className="post-content-ctm"
          >
            <motion.div
              ref={ref}
              initial="hidden"
              animate={isInView || hasPlayed ? "show" : "hidden"}
              variants={sectionShow}
              className="container-fluid-lg"
            >
              <div className="ctm-content-single bg-white mt-[175px] shadow-at-bottom">
                {post?.featuredImage && (
                  <motion.div
                    variants={animFade}
                    className="banner-img w-full mx-auto"
                  >
                    <ImageOpt
                      imgData={post.featuredImage.node.mediaItemUrl}
                      imgAlt={post.featuredImage.node.altText}
                      imgLoad="eager"
                      imgClass="w-full h-[550px] block object-cover object-center mdscreen:h-[220px]"
                      width={1440}
                      height={830}
                    />
                  </motion.div>
                )}
                <motion.div
                  variants={animFade}
                  className="pt-70 lg:px-[118px] px-20 top-title"
                >
                  <ul>
                    <div className="post-cat-name flex items-center justify-center lg:gap-[20px] gap-[10px] flex-wrap">
                      {post?.date && (
                        <li>
                          <div className="post-date">{parse(post.date)}</div>
                        </li>
                      )}
                    </div>
                  </ul>
                  {post?.title && (
                    <h1 className="post-name py-20">{parse(post.title)}</h1>
                  )}

                  {post?.excerpt && (
                    <div className="content pt-20 text-center pb-40 border-b-[1px] border-solid border-everglade/10">
                      <p className="md:max-w-[734px] mx-auto w-full ">
                        {parse(post?.excerpt)}
                      </p>
                    </div>
                  )}
                </motion.div>
                <div className="post-content-details py-100 lg:px-80 xl:px-[116px] px-10 mdscreen:py-40">
                  {post?.content ? (
                    <motion.div variants={animFade} className="post-content">
                      {parse(post.content)}
                    </motion.div>
                  ) : (
                    <motion.div variants={animFade} className="post-content">
                      {parse(
                        post.components.siteComponents[0].cgcGeneralContent.replace(
                          /<p>\s*(<img[^>]+>)\s*<\/p>/gi,
                          "$1"
                        )
                      )}
                    </motion.div>
                  )}
                  <div className="Share-ctm lg:pt-65 pt-40">
                    <div className="flex items-center justify-center">
                      {post?.categories?.nodes?.length > 0 && (
                        <p className="text-timbergreen text-opacity-40">
                          {post.categories.nodes.map((category, index) => (
                            <React.Fragment key={index}>
                              <Link
                                to={category.uri}
                                className="text-timbergreen hover:text-opacity-60"
                              >
                                {category.name}
                              </Link>
                              {index < post.categories.nodes.length - 1 &&
                                " | "}
                            </React.Fragment>
                          ))}
                        </p>
                      )}
                    </div>
                    <div className="social-media pt-35 mdscreen:pt-25">
                      <ul>
                        <li className="flex justify-center items-center mdscreen2:order-1 mdscreen2:w-full">
                          <span className="text-[22px] tracking-0.04em font-15 font-heading italic text-everglade normal-case">
                            Share this post:
                          </span>
                          <div className="flex items-center gap-[12px] pl-20 mdscreen:pl-15">
                            <Link
                              to={`mailto:?Subject=${
                                post.title
                              }&Body=https://www.resplendentceylon.com${`${post.uri}`}`}
                              title="Mail to"
                              target="_blank"
                            >
                              <img
                                src="../../images/mail.svg"
                                alt="Mail"
                                height={24}
                                width={24}
                                loading="lazy"
                                className="h-[28px] w-[28px] object-center object-contain"
                              />
                            </Link>

                            <Link
                              to={`https://www.facebook.com/sharer/sharer.php?u=https://www.resplendentceylon.com${`${post.uri}`}`}
                              title="Facebook"
                              target="_blank"
                            >
                              <img
                                src="../../images/facebook.svg"
                                alt="Facebook"
                                height={24}
                                width={24}
                                loading="lazy"
                                className="h-[28px] w-[28px] object-center object-contain"
                              />
                            </Link>
                            <Link
                              to={`https://www.linkedin.com/shareArticle?mini=true&url=https://www.resplendentceylon.com${`${post.uri}`}`}
                              title="LinkedIn"
                              target="_blank"
                            >
                              <img
                                src="../../images/linkedin.svg"
                                alt="LinkedIn"
                                height={24}
                                width={24}
                                loading="lazy"
                                className="h-[28px] w-[28px] object-center object-contain"
                              />
                            </Link>
                            <button
                              onClick={copyToClipboard}
                              className="copy-link-button"
                            >
                              <img
                                src="../../images/link.svg"
                                alt="Copy Link"
                                height={24}
                                width={24}
                                loading="lazy"
                                className="h-[28px] w-[28px] object-center object-contain"
                              />
                            </button>
                            {copySuccess && (
                              <span className="copy-success">
                                {copySuccess}
                              </span>
                            )}
                          </div>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
                <motion.section className="zigzag-img bg-timbergreen lg:py-[74px] py-40">
                  <motion.div className="container-fluid-md">
                    <div className="flex flex-wrap items-center gap-y-[30px]">
                      {cta.blogSingleCtaImage && (
                        <div className="mdscreen2:w-full w-6/12">
                          <motion.div variants={animFade} className="img">
                            <ImageOpt
                              imgData={cta.blogSingleCtaImage?.mediaItemUrl}
                              imgAlt={cta.blogSingleCtaImage?.altText}
                              width={417}
                              height={473}
                              imgLoad="lazy"
                            />
                          </motion.div>
                        </div>
                      )}
                      <div className="mdscreen2:w-full w-6/12 md2:pl-[77px] xl:!pr-95">
                        <div className="mr-auto">
                          {cta.blogSinglePreHeading && (
                            <div className="top-contnet pb-10 white">
                              <span className="text-11 font-300 tracking-05em uppercase">
                                {cta.blogSinglePreHeading}
                              </span>
                            </div>
                          )}
                          {cta.blogSingleCtaHeading && (
                            <motion.div
                              variants={animFade}
                              className="title title-white capitalize pb-[16px]"
                            >
                              <h4>{parse(cta.blogSingleCtaHeading)}</h4>
                            </motion.div>
                          )}
                          {cta.blogSingleCtaDescription && (
                            <motion.div
                              variants={animFade}
                              className="content white max-w-[486px] w-full lgscreen:max-w-full"
                            >
                              {parse(cta.blogSingleCtaDescription)}
                            </motion.div>
                          )}
                          {cta.validity && (
                            <motion.div
                              variants={animFade}
                              className="content white max-w-[486px] w-full lgscreen:max-w-full text-white pt-15 content-small"
                            >
                              <span>{cta.validity}</span>
                            </motion.div>
                          )}
                          {cta.blogSingleCtaButton && (
                            <motion.div
                              variants={animFade}
                              className="mt-20 inline-block mdscreen:mt-10"
                            >
                              <Link
                                to={cta.blogSingleCtaButton?.url || "/"}
                                className="btn-link btn-link-white flex flex-wrap gap-x-[10px]"
                                target={
                                  cta.blogSingleCtaButton?.target || "_self"
                                }
                              >
                                {cta.blogSingleCtaButton?.title ||
                                  "discover all journeys"}{" "}
                                <img
                                  src="../../images/btn-white-arrow.svg"
                                  width={12}
                                  height={17}
                                  loading="lazy"
                                  alt="Arrow"
                                />
                              </Link>
                            </motion.div>
                          )}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </motion.section>
                <motion.div
                  variants={animFade}
                  className="pagination paginationSingle py-60 lgscreen:py-45 px-60 lgscreen:px-45 mdscreen:px-20"
                >
                  <ul className="flex flex-wrap !justify-between smscreen2:!justify-center gap-4 !pt-0 gap-x-[20px] gap-y-[20px]">
                    <motion.li
                      className={`prev flex items-center ${
                        prevPageCursor === "" ? " disabled" : ""
                      }`}
                    >
                      {/* <div className="icon-img border-[1px] border-solid border-everglade rounded-100 w-[36px] h-[36px] flex items-center justify-center">
                        <img
                          src="../../images/prev-arrow.svg"
                          className=""
                          width={17}
                          height={17}
                          loading="lazy"
                          alt="Arrow"
                        />
                      </div> */}
                      {prevPageCursor === "" ? (
                        <Link
                          to="#"
                          className="btn btn-border flex flex-wrap smscreen2:justify-center items-center gap-x-2 gold-clr-btn p-0"
                        >
                          <div className="icon-img border-[1px] border-solid border-everglade rounded-100 w-[36px] h-[36px] flex items-center justify-center mr-20">
                            <img
                              src="../../images/prev-arrow.svg"
                              className=""
                              width={17}
                              height={17}
                              loading="lazy"
                              alt="Arrow"
                            />
                          </div>
                          <p className="text-14 tracking-05em m-0">
                            previous blog
                          </p>
                        </Link>
                      ) : (
                        <Link
                          to={prevPageCursor}
                          className="pre-enable btn btn-border flex flex-wrap smscreen2:justify-center items-center gap-x-2 gold-clr-btn"
                        >
                          <p className="text-14 tracking-05em">previous blog</p>
                        </Link>
                      )}
                    </motion.li>

                    <motion.li
                      className={`next flex items-center lg:flex-row-reverse ${
                        nextPageCursor === "" ? " disabled" : ""
                      }`}
                    >
                      {nextPageCursor === "" ? (
                        <Link
                          to="#"
                          className="next-disable btn btn-border flex flex-wrap smscreen2:justify-center items-center gap-x-2 gold-clr-btn p-0 flex-row-reverse"
                        >
                          <div className="icon-img border-[1px] border-solid border-everglade rounded-100 w-[36px] h-[36px] flex items-center justify-center ml-20">
                            <img
                              src="../../images/next-arrow.svg"
                              className=""
                              width={17}
                              height={17}
                              loading="lazy"
                              alt="Arrow"
                            />
                          </div>
                          <p className="text-14 tracking-05em m-0">
                            next blog{" "}
                          </p>
                        </Link>
                      ) : (
                        <Link
                          to={nextPageCursor}
                          className="btn btn-border flex flex-wrap items-center smscreen2:justify-center gap-x-2 gold-clr-btn"
                        >
                          <p className="text-14 tracking-05em">next blog</p>
                        </Link>
                      )}
                    </motion.li>
                  </ul>
                </motion.div>
              </div>
            </motion.div>
          </motion.section>
          <div
            className={`slider-with-content my-55 lgscreen:my-30 py-80 lgscreen:py-40 ${
              module.extraClass ? ` ${module.extraClass}` : ""
            }`}
            id={module.extraId}
          >
            <div className="container-fluid-md !pr-0">
              <div className="flex flex-wrap items-center">
                <div className="lg:w-4/12 w-full relative">
                  <div className="relative max-w-[328px] w-full lgscreen:max-w-[556px] lgscreen:mx-auto left-content laptop:pr-[80px] lgscreen:text-center lgscreen:pr-0">
                    {cta.blogRelatedHeading && (
                      <motion.div
                        variants={animFade}
                        className="title title-everglade"
                      >
                        <h2>{parse(cta.blogRelatedHeading)}</h2>
                      </motion.div>
                    )}
                    {cta.blogRelatedDescription && (
                      <motion.div
                        variants={animFade}
                        className="content pt-10 max-w-[309px] w-full lgscreen:max-w-full relative z-9"
                      >
                        {parse(cta.blogRelatedDescription)}
                      </motion.div>
                    )}
                    {cta.blogRelatedButton && (
                      <motion.div
                        variants={animFade}
                        className="mt-30 inline-block"
                      >
                        <Link
                          to={cta.blogRelatedButton.url}
                          target={cta.blogRelatedButton.target}
                          className="btn-link flex flex-wrap gap-x-[10px]"
                        >
                          {cta.blogRelatedButton.title}
                          <img
                            src="../../images/btn-arrow.svg"
                            width={12}
                            height={17}
                            loading="lazy"
                            alt="Arrow"
                          />
                        </Link>
                      </motion.div>
                    )}
                  </div>
                </div>
                {cta?.selectRelatedPosts?.length > 0 && (
                  <motion.div
                    variants={animFade}
                    className="lg:w-8/12 w-full relative"
                  >
                    <Swiper
                      className="lgscreen:!pt-[20px]"
                      spaceBetween={45}
                      loop={false}
                      slidesPerView={"1"}
                      speed={1500}
                      navigation={{
                        nextEl: ".s-btn-next-1",
                        prevEl: ".s-btn-prev-1",
                        disabledClass: "btn-disabled",
                      }}
                      modules={[Autoplay, Navigation]}
                      breakpoints={{
                        640: {
                          slidesPerView: 1,
                          spaceBetween: 20,
                        },
                        768: {
                          slidesPerView: 1,
                          spaceBetween: 40,
                        },
                        1024: {
                          slidesPerView: 1.87,
                          spaceBetween: 50,
                        },
                      }}
                    >
                      {cta.selectRelatedPosts?.map((item, i) => (
                        <SwiperSlide className="!bg-transparent" key={i}>
                          <Link
                            to={item.uri}
                            className="slider-block p-[28px] mdscreen:p-[16px] pb-[54px] block bg-white shadow-cardBox2 w-full h-full"
                          >
                            {item?.featuredImage && (
                              <div className="img">
                                <ImageOpt
                                  imgData={item.featuredImage.node.mediaItemUrl}
                                  imgAlt={item.featuredImage.node.altText}
                                  width={404}
                                  height={241}
                                  imgLoad="lazy"
                                />
                              </div>
                            )}
                            <div className="s-detail p-[12px] pt-[28px] mdscreen:pt-[16px]">
                              {item?.date && (
                                <div className="pre-heading">
                                  <span>{item.date}</span>
                                </div>
                              )}
                              {item?.title && (
                                <div className="title pt-[14px]">
                                  <h6>{item.title}</h6>
                                </div>
                              )}
                              {item?.excerpt && (
                                <div className="content pt-[14px] max-w-[322px] w-full lgscreen:max-w-full">
                                  {parse(
                                    item.excerpt.length > 150
                                      ? `${item.excerpt.slice(0, 150)}...`
                                      : item.excerpt
                                  )}
                                </div>
                              )}
                            </div>
                          </Link>
                        </SwiperSlide>
                      ))}
                    </Swiper>
                    <div className="swiper-arrow absolute lgscreen:relative lgscreen:left-0 lgscreen:flex-row lgscreen:top-0 lgscreen:translate-y-0 -left-[64px] top-50per translate-y-minus_50 flex flex-col-reverse gap-y-[10px] gap-x-5 lgscreen:justify-center lgscreen:pt-[40px]">
                      <button className="s-btn-prev-1 z-9 w-[36px] h-[36px] rounded-100 border-[0.5px] border-everglade flex flex-wrap justify-center items-center transition-all duration-300">
                        <img
                          src="../../images/prev-arrow.svg"
                          width={17}
                          height={17}
                          loading="lazy"
                          alt="Arrow"
                        />
                      </button>
                      <button className="s-btn-next-1 z-9 w-[36px] h-[36px] rounded-100 border-[0.5px] border-everglade flex flex-wrap justify-center items-center transition-all duration-300">
                        <img
                          src="../../images/next-arrow.svg"
                          width={17}
                          height={17}
                          loading="lazy"
                          alt="Arrow"
                        />
                      </button>
                    </div>
                  </motion.div>
                )}
              </div>
            </div>
          </div>
        </section>
      </Layout>
    </div>
  )
}

export default BlogTemplate
export const blogPostQuery = graphql`
  query blogById($id: String) {
    post: wpPost(id: { eq: $id }) {
      id
      title
      uri
      link
      content
      categories {
        nodes {
          name
          uri
        }
      }
      components {
        siteComponents {
          ... on WpPost_Components_SiteComponents_CompGeneralContent {
            cgcGeneralContent
          }
        }
      }
      seo {
        metaDesc
        canonical
        opengraphDescription
        metaRobotsNofollow
        metaRobotsNoindex
        opengraphTitle
        opengraphType
        opengraphUrl
        opengraphSiteName
        title
        twitterDescription
        twitterTitle
        opengraphImage {
          publicUrl
        }
        twitterImage {
          publicUrl
        }
      }
      date(formatString: "D MMMM yyyy")
      excerpt
      featuredImage {
        node {
          altText
          mediaItemUrl
        }
      }
      author {
        node {
          name
        }
      }
    }
    wp {
      themeGeneralSettings {
        fluxDna {
          blogSingleCtaButton {
            target
            title
            url
          }
          blogSingleCtaDescription
          blogSingleCtaHeading
          blogSingleCtaImage {
            altText
            mediaItemUrl
            title
          }
          blogSinglePreHeading
          validity
          blogRelatedButton {
            target
            title
            url
          }
          blogRelatedDescription
          blogRelatedHeading
          selectRelatedPosts {
            ... on WpPost {
              id
              date
              featuredImage {
                node {
                  altText
                  mediaItemUrl
                  title
                }
              }
              title
              excerpt
              uri
            }
          }
        }
      }
    }
  }
`
